// src/features/players/components/PlayerTableRow.tsx

import { useCallback } from 'react';
import { Table, Checkbox, Badge, Tooltip, ActionIcon, Menu, Text } from '@mantine/core';
import { useNavigate } from '@tanstack/react-router';
import {
  IconEdit,
  IconCopy,
  IconPlayerPlay,
  IconPlayerPause,
  IconTrash,
  IconDotsVertical,
  IconStar,
} from '@tabler/icons-react';
import { useSelection } from '@/shared/contexts/SelectionContext';
import type { PlayerGridRow } from '@/lib/types/Player';

interface PlayerTableRowProps {
  row: PlayerGridRow;
  index: number;
  allIds: number[];
  onCopy: (row: PlayerGridRow) => void;
  onActivateToggle: (row: PlayerGridRow) => void;
  onDelete: (row: PlayerGridRow) => void;
}

export function PlayerTableRow({
  row,
  index,
  allIds,
  onCopy,
  onActivateToggle,
  onDelete,
}: PlayerTableRowProps) {
  const navigate = useNavigate();
  const { isSelected, toggle, toggleRange, selectOnly } = useSelection();
  const selected = isSelected(row.playerId);

  const handleCheckboxChange = useCallback(
    (e: React.MouseEvent) => {
      if (e.shiftKey) {
        toggleRange(row.playerId, index, allIds);
      } else if (e.metaKey || e.ctrlKey) {
        toggle(row.playerId, index);
      } else {
        selectOnly(row.playerId, index);
      }
    },
    [row.playerId, index, allIds, toggle, toggleRange, selectOnly],
  );

  const handleRowClick = useCallback(
    (e: React.MouseEvent) => {
      // Don't navigate if clicking on checkbox or action menu
      if ((e.target as HTMLElement).closest('[data-no-nav]')) return;
      navigate({ to: '/players/$id', params: { id: row.displayId } });
    },
    [navigate, row.displayId],
  );

  return (
    <Table.Tr
      bg={selected ? 'var(--mantine-color-blue-light)' : undefined}
      onClick={handleRowClick}
      style={{ cursor: 'pointer' }}
    >
      <Table.Td w={48} onClick={(e) => e.stopPropagation()} data-no-nav>
        <Checkbox
          checked={selected}
          onChange={() => {}}
          onClick={handleCheckboxChange}
          aria-label={`Select ${row.label}`}
        />
      </Table.Td>

      <Table.Td>
        <Text size="sm" fw={500} lineClamp={1}>
          {row.label}
        </Text>
        <Text size="xs" c="dimmed">{row.displayId}</Text>
      </Table.Td>

      <Table.Td w={100}>
        <Badge
          color={row.statusColor}
          variant="light"
          size="sm"
        >
          {row.statusLabel}
        </Badge>
      </Table.Td>

      <Table.Td w={60} ta="center">
        {row.isDefault && (
          <Tooltip label="Default Player">
            <IconStar size={16} style={{ color: 'var(--mantine-color-yellow-6)' }} />
          </Tooltip>
        )}
      </Table.Td>

      <Table.Td w={120}>
        <Tooltip label={row.createdTooltip}>
          <Text size="sm" c="dimmed">{row.createdFormatted}</Text>
        </Tooltip>
      </Table.Td>

      <Table.Td w={120}>
        <Tooltip label={row.updatedTooltip}>
          <Text size="sm" c="dimmed">{row.updatedFormatted}</Text>
        </Tooltip>
      </Table.Td>

      <Table.Td w={48} onClick={(e) => e.stopPropagation()} data-no-nav>
        <Menu position="bottom-end" withArrow>
          <Menu.Target>
            <ActionIcon variant="subtle" color="gray" size="sm">
              <IconDotsVertical size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconEdit size={14} />}
              onClick={() => navigate({ to: '/players/$id', params: { id: row.displayId } })}
            >
              Edit
            </Menu.Item>
            <Menu.Item
              leftSection={<IconCopy size={14} />}
              onClick={() => onCopy(row)}
            >
              Copy
            </Menu.Item>
            <Menu.Item
              leftSection={row.enabled ? <IconPlayerPause size={14} /> : <IconPlayerPlay size={14} />}
              onClick={() => onActivateToggle(row)}
            >
              {row.enabled ? 'Deactivate' : 'Activate'}
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item
              leftSection={<IconTrash size={14} />}
              color="red"
              onClick={() => onDelete(row)}
            >
              Delete
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Table.Td>
    </Table.Tr>
  );
}
